(() => {
  angular.module('app').controller('host.views.hubSyncRepair.index', HubSyncRepairController);

  HubSyncRepairController.$inject = [
    '$scope',
    '$filter',
    'abp.services.app.hubSyncRepair',
    'Hms.GuaranteeLetters.GuaranteeLetterStatus',
    'Hms.GuaranteeLetters.GuaranteeLetterState',
    'Hms.ServiceRequests.ServiceRequestType',
    'Hms.ServiceRequests.ServiceRequestStatus',
    'Hms.ServiceRequests.ServiceRequestQueryStatus',
  ];

  // Shown when the letter has no value for a field.
  const NO_VALUE = '\u2014';

  // Mirrors Hms.HubSyncRepair.Dto.HubSyncRepairAction [HM-11238], in the order the page lists them.
  const ACTIONS = [
    { id: 1, key: 'InitialLetter', label: 'HubSyncRepairActionInitialLetter' },
    { id: 2, key: 'FinalLetter', label: 'HubSyncRepairActionFinalLetter' },
    { id: 3, key: 'Cancellation', label: 'HubSyncRepairActionCancellation' },
    { id: 4, key: 'QueryReply', label: 'HubSyncRepairActionQueryReply' },
  ];

  // Mirrors Hms.HubSyncRepair.Dto.HubSyncRepairCheck [HM-11239]. A missing Hub patient record is
  // a caution, not an instruction: the patient is usually not international (criterion 5).
  const CHECKS = {
    1: { label: 'HubSyncRepairCheckHubPatientRecord', isCaution: true },
    2: { label: 'HubSyncRepairCheckInternationalPatients', isCaution: false },
    3: { label: 'HubSyncRepairCheckHubProvider', isCaution: false },
  };

  // Mirrors Hms.HubSyncRepair.Dto.HubSyncRepairCheckResult [HM-11239].
  const CHECK_RESULT = { Passed: 1, Failed: 2, NotChecked: 3 };

  // Mirror the back end's RepairSendStatus, HubRetriggerOutcome, HubRetriggerStep and
  // HubSyncRepairCaseAnswer [HM-11240, HM-11241].
  const SEND_STATUS = { Sent: 0, Failed: 1, NeedsHubAdminCheck: 2, Refused: 3, NeedsCaseAnswer: 4 };
  const OUTCOME = { Accepted: 0, Rejected: 1, Unknown: 2, ConfirmedByOperator: 3 };
  const STEP = { CreateCase: 0, SubmitLetter: 1, UpdateCase: 2, AddMessage: 3 };
  const CASE_ANSWER = { CreateNew: 1, UseExisting: 2 };

  // A Hub case id as Hub admin shows it.
  const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Hub Sync Repair [HM-11237]. The page never reads the Hub and never names a Hub status, so the
  // operator's own Hub admin check stays the diagnosis. From HM-11241 it can send to the Hub.
  function HubSyncRepairController(
    $scope,
    $filter,
    hubSyncRepairSvc,
    enumGuaranteeLetterStatus,
    enumGuaranteeLetterState,
    enumServiceRequestType,
    enumServiceRequestStatus,
    enumServiceRequestQueryStatus
  ) {
    const vm = this;
    vm.loading = 0;

    vm.direction = null; // 'outbound' | 'inbound'
    vm.inboundProblem = null; // 'outcome' | 'query'
    vm.letterNumber = null;
    vm.letter = null;
    vm.lookedUpDirection = null;
    vm.lookedUpLetterNumber = null;

    // Outbound pick [HM-11238]: what is missing on the Hub, and for a query reply, which query.
    vm.action = null;
    vm.queryId = null;
    vm.actions = [];
    vm.replyableQueries = [];

    // Checks panel [HM-11239]: the picked action's checks, in the order they ran. Picking an action
    // re-runs them on the server, so a host admin's fix is picked up without a new look-up.
    vm.checks = [];
    vm.checking = false;
    vm.checksReady = false; // the server answered for the current pick
    vm.actionGone = false; // the re-check found the picked action is no longer offered
    vm.checkFailed = false; // the re-check itself failed (network or server error)
    let checkRun = 0;

    // Sending [HM-11241]. One result at a time; the screens below the checks are driven by it.
    vm.caseAnswers = CASE_ANSWER;
    vm.send = newSendState();

    vm.canLookUp = canLookUp;
    vm.lookUp = lookUp;
    vm.copyHubCaseId = copyHubCaseId;
    vm.isPickComplete = isPickComplete;
    vm.letterStatusName = letterStatusName;
    vm.letterStateName = letterStateName;
    vm.requestTypeName = requestTypeName;
    vm.requestStatusName = requestStatusName;
    vm.queryStatusName = queryStatusName;
    vm.hasNoCaseToCancel = hasNoCaseToCancel;
    vm.hasHubQueriesButNoReply = hasHubQueriesButNoReply;
    vm.allChecksPassed = allChecksPassed;
    vm.hasFailedCheck = hasFailedCheck;
    vm.runChecks = runChecks;
    vm.isLetterAction = isLetterAction;
    vm.isFinalLetter = isFinalLetter;
    vm.isCancellation = isCancellation;
    vm.isQueryReply = isQueryReply;
    vm.selectedQuery = selectedQuery;
    vm.repliedByText = repliedByText;
    vm.uncheckedLabel = uncheckedLabel;
    vm.alreadyThereHeading = alreadyThereHeading;
    vm.isSendAction = isSendAction;
    vm.mustSendInitialFirst = mustSendInitialFirst;
    vm.canSendLetter = canSendLetter;
    vm.isCaseIdInvalid = isCaseIdInvalid;
    vm.confirmSend = confirmSend;
    vm.sendButtonLabel = sendButtonLabel;
    vm.sendScreen = sendScreen;
    vm.sentMessage = sentMessage;
    vm.unknownCheckMessage = unknownCheckMessage;
    vm.alreadySentMessage = alreadySentMessage;
    vm.isUnknownCaseCreation = isUnknownCaseCreation;
    vm.canRecordAsReceived = canRecordAsReceived;
    vm.recordAsReceived = recordAsReceived;
    vm.checkAnotherLetter = checkAnotherLetter;

    // A result belongs to the letter and direction it was looked up for; changing either
    // clears it, and lookUp() drops a response that arrives after either changed, so the
    // instructions can never describe a different letter. The inbound problem only changes
    // which events are listed, so it keeps the result.
    $scope.$watchGroup(['vm.direction', 'vm.letterNumber'], (newValues, oldValues) => {
      if (newValues !== oldValues) setLetter(null, null);
    });

    // Changing what is missing clears the query choice, so a reply is never sent for a stale pick.
    // It also swaps in that action's checks [HM-11239].
    $scope.$watch('vm.action', (newValue, oldValue) => {
      if (newValue !== oldValue) {
        vm.queryId = null;
        vm.send = newSendState(vm.letter);
      }
      runChecks();
    });

    // Each query is locked on its own [HM-11244], so a different query starts a fresh send.
    $scope.$watch('vm.queryId', (newValue, oldValue) => {
      if (newValue !== oldValue) vm.send = newSendState(vm.letter);
    });

    function canLookUp() {
      if (vm.loading || !vm.letterNumber || !vm.letterNumber.trim()) return false;
      return vm.direction === 'outbound' || (vm.direction === 'inbound' && !!vm.inboundProblem);
    }

    function lookUp() {
      if (!canLookUp()) return;

      const { direction } = vm;
      const letterNumber = vm.letterNumber.trim();
      setLetter(null, null);
      vm.loading += 1;
      hubSyncRepairSvc
        .lookUpLetterAsync({ letterNumber })
        .success((output) => {
          // The fields stay editable while the request is in flight; ignore a late response
          // for a letter number or direction the operator has since changed.
          const currentLetterNumber = vm.letterNumber ? vm.letterNumber.trim() : '';
          if (vm.direction !== direction || currentLetterNumber !== letterNumber) return;

          vm.lookedUpLetterNumber = letterNumber;
          setLetter(output, direction);
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    // One place sets the result, so the pick and its lists always describe the letter on screen.
    function setLetter(letter, direction) {
      vm.letter = letter;
      vm.lookedUpDirection = direction;
      vm.action = null;
      vm.queryId = null;
      checkRun += 1; // drops any check still running for the previous letter
      vm.checks = [];
      vm.checking = false;
      vm.checksReady = false;
      vm.actionGone = false;
      vm.send = newSendState(letter);
      vm.checkFailed = false;

      // The back end decides what this letter supports; the page only lists it [HM-11238].
      const offered = (letter && letter.actions) || [];
      vm.actions = ACTIONS.filter((a) => offered.includes(a.id));
      vm.replyableQueries = ((letter && letter.queries) || []).filter((q) => q.canReply);
    }

    // The back end runs every offered action's checks; the page shows the picked one [HM-11239].
    // Null when the action is no longer offered; empty when it needs no checks (the query reply, HM-11244).
    function checksFor(actionKey) {
      const action = _.find(ACTIONS, (a) => a.key === actionKey);
      const entry = action && vm.letter && _.find(vm.letter.checks, (c) => c.action === action.id);
      if (!entry) return null;

      return entry.checks.map((check) => {
        const definition = CHECKS[check.check] || { label: '', isCaution: false };
        const failed = check.result === CHECK_RESULT.Failed;
        let badge = 'NotChecked';
        if (check.result === CHECK_RESULT.Passed) badge = 'Passed';
        else if (failed) badge = definition.isCaution ? 'Caution' : 'Blocked';

        return {
          label: definition.label,
          message: check.message,
          badge,
          isFailed: failed,
        };
      });
    }

    // Re-reads the letter for fresh checks. The pick stays as it is; an answer for an older pick is
    // ignored. What a send may have changed since the look-up is taken too [HM-11242]: after the
    // initial letter is sent, the case is known, so the final letter must not ask for it again.
    function runChecks() {
      checkRun += 1;
      const run = checkRun;
      const { action } = vm;
      vm.checks = [];
      vm.checksReady = false;
      vm.actionGone = false;
      vm.checkFailed = false;

      if (!action || !vm.letter) {
        vm.checking = false;
        return;
      }

      vm.checking = true;
      hubSyncRepairSvc
        .lookUpLetterAsync({ letterNumber: vm.lookedUpLetterNumber })
        .success((output) => {
          if (run !== checkRun || !vm.letter) return;
          vm.letter.checks = output.checks;
          vm.letter.hubCaseId = output.hubCaseId;
          vm.letter.requests = output.requests;
          vm.letter.needsCaseAnswer = output.needsCaseAnswer;
          vm.letter.finalLetterNeedsCaseAnswer = output.finalLetterNeedsCaseAnswer;
          vm.letter.finalBillAmount = output.finalBillAmount;
          // Nothing sent yet for this pick: ask about the case only if the server still needs it.
          // After a send, the result decides (a refused operator case asks again).
          if (!vm.send.result && !vm.send.sending) vm.send.needsCaseAnswer = needsCaseAnswerFor(vm.letter);
          // The query reply's preview and offer come from the queries [HM-11244]: take them fresh too.
          vm.letter.queries = output.queries;
          vm.replyableQueries = (output.queries || []).filter((q) => q.canReply);
          const checks = checksFor(action);
          vm.checks = checks || [];
          // The letter changed since the look-up (say, it was cancelled, or the Hub resolved the picked query).
          vm.actionGone = checks === null || (action === 'QueryReply' && !!vm.queryId && !selectedQuery());
          vm.checksReady = !vm.actionGone;
        })
        .error(() => {
          // ABP has already shown the error; the panel offers a retry instead of sitting empty.
          if (run === checkRun) vm.checkFailed = true;
        })
        .finally(() => {
          if (run === checkRun) vm.checking = false;
        });
    }

    // Nothing blocks the send [HM-11239]. An action with no checks passes once the server has answered.
    function allChecksPassed() {
      return vm.checksReady && _.every(vm.checks, (c) => c.badge === 'Passed');
    }

    function hasFailedCheck() {
      return _.some(vm.checks, (c) => c.isFailed);
    }

    // A query reply also needs the query [criterion 5].
    function isPickComplete() {
      if (!vm.action) return false;
      return vm.action !== 'QueryReply' || !!vm.queryId;
    }

    function enumName(enumObj, id) {
      const match = _.find(enumObj.values, (v) => v.id === id);
      return match ? App.localize(match.name) : NO_VALUE;
    }

    function letterStatusName() {
      return enumName(enumGuaranteeLetterStatus, vm.letter.status);
    }

    // Letter type = the letter's state, Initial or Final; none shows a dash (decision 4).
    function letterStateName() {
      if (vm.letter.state == null) return NO_VALUE;
      return enumName(enumGuaranteeLetterState, vm.letter.state);
    }

    // Uses the back end's ServiceRequestType_* keys; AddOn's key is TopupAddon.
    function requestTypeName(id) {
      const match = _.find(enumServiceRequestType.values, (v) => v.id === id);
      if (!match) return NO_VALUE;
      return App.localize(match.name === 'AddOn' ? 'ServiceRequestType_TopupAddon' : `ServiceRequestType_${match.name}`);
    }

    function requestStatusName(id) {
      return enumName(enumServiceRequestStatus, id);
    }

    function queryStatusName(id) {
      return enumName(enumServiceRequestQueryStatus, id);
    }

    // Rejected or Cancelled, but no recorded Hub case to cancel (decision 6).
    function hasNoCaseToCancel() {
      const s = vm.letter.status;
      const isDead =
        s === enumGuaranteeLetterStatus.Rejected.id || s === enumGuaranteeLetterStatus.Cancelled.id;
      return isDead && !vm.letter.hubCaseId;
    }

    // Queries came from the Hub, but none has a reply waiting to be sent (decision 2).
    function hasHubQueriesButNoReply() {
      return _.some(vm.letter.queries, (q) => q.isFromHub) && vm.replyableQueries.length === 0;
    }

    // ----- Sending the initial [HM-11241] or final [HM-11242] letter, or the cancellation [HM-11243] -----

    // The two letters share the send screens; only the wording and the endpoint differ.
    function isLetterAction() {
      return vm.action === 'InitialLetter' || vm.action === 'FinalLetter';
    }

    function isFinalLetter() {
      return vm.action === 'FinalLetter';
    }

    // The cancellation shares the same screens. It goes to the recorded Hub case, so it never asks about one.
    function isCancellation() {
      return vm.action === 'Cancellation';
    }

    // The query reply shares the same screens too [HM-11244]. It goes to the query's own Hub thread.
    function isQueryReply() {
      return vm.action === 'QueryReply';
    }

    function isSendAction() {
      return isLetterAction() || isCancellation() || isQueryReply();
    }

    // The picked query as the server last described it, with the preview of what its reply would send.
    function selectedQuery() {
      return _.find(vm.replyableQueries, (q) => q.id === vm.queryId) || null;
    }

    function repliedByText(preview) {
      const when = preview.repliedTime ? $filter('momentFormat')(preview.repliedTime, 'L LT') : NO_VALUE;
      return App.localize('HubSyncRepairRepliedByAt', preview.repliedByName || NO_VALUE, when);
    }

    // The Unknown tick names what the operator looked for in Hub admin.
    function uncheckedLabel() {
      if (isCancellation()) return 'HubSyncRepairCheckedNotCancelled';
      if (isQueryReply()) return 'HubSyncRepairCheckedNoReply';
      return 'HubSyncRepairCheckedNotThere';
    }

    function alreadyThereHeading() {
      if (isCancellation()) return 'HubSyncRepairCaseIsCancelled';
      if (isQueryReply()) return 'HubSyncRepairReplyIsThere';
      return 'HubSyncRepairLetterIsThere';
    }

    // Fresh for every letter and every pick. The case question shows when the look-up says the send
    // needs a case and nothing tells us the Hub already has one (HM-11241 criterion 5): the initial
    // letter would create one; the final letter never does, so it must be told which [HM-11242].
    function needsCaseAnswerFor(letter) {
      if (isCancellation() || isQueryReply()) return false;
      return isFinalLetter()
        ? !!(letter && letter.finalLetterNeedsCaseAnswer)
        : !!(letter && letter.needsCaseAnswer);
    }

    function newSendState(letter) {
      return {
        needsCaseAnswer: needsCaseAnswerFor(letter),
        caseAnswer: null,
        hubCaseId: '',
        hubAdminChecked: false,
        // Once the operator confirmed the Hub admin check for this letter, it holds for the rest of
        // this send, so a follow-up question (the case) does not send them back to the check.
        hubAdminConfirmed: false,
        sending: false,
        recording: false,
        recorded: false,
        result: null,
      };
    }

    function isGuid(value) {
      return !!value && GUID_PATTERN.test(value.trim());
    }

    function isCaseIdInvalid() {
      const s = vm.send;
      return s.caseAnswer === CASE_ANSWER.UseExisting && !!s.hubCaseId && !isGuid(s.hubCaseId);
    }

    // [HM-11242 decision 3] No case in Hub admin for a final letter: the initial letter is missing too,
    // so it goes first. Nothing is sent from here.
    function mustSendInitialFirst() {
      return isFinalLetter() && vm.send.needsCaseAnswer && vm.send.caseAnswer === CASE_ANSWER.CreateNew;
    }

    function isCaseAnswered() {
      const s = vm.send;
      if (!s.needsCaseAnswer) return true;
      if (s.caseAnswer === CASE_ANSWER.CreateNew) return !isFinalLetter();
      return s.caseAnswer === CASE_ANSWER.UseExisting && isGuid(s.hubCaseId);
    }

    // Which screen the last answer calls for. 401/403 is a setup fault with no Try again
    // (HM-11240 criterion 8).
    function sendScreen() {
      const r = vm.send.result;
      if (vm.send.recorded) return 'recorded';
      if (!r) return null;
      if (r.status === SEND_STATUS.Sent) return 'sent';
      if (r.status === SEND_STATUS.Refused) return 'refused';
      if (r.status === SEND_STATUS.NeedsHubAdminCheck) return 'unknown';
      if (r.status === SEND_STATUS.Failed) {
        if (r.outcome === OUTCOME.Unknown) return 'unknown';
        if (r.statusCode === 401 || r.statusCode === 403) return 'setupFault';
        return 'notSent';
      }
      return null; // NeedsCaseAnswer: the question is the screen
    }

    // The button shows for a letter once every check passed, and not on a screen that ends the flow.
    function canSendLetter() {
      const screen = sendScreen();
      if (!isSendAction() || vm.checking || !allChecksPassed()) return false;
      if (vm.send.sending || vm.send.recording) return false;
      if (screen === 'sent' || screen === 'refused' || screen === 'setupFault' || screen === 'recorded') return false;
      // An unanswered case creation is checked by answering the case question; anything else by
      // the tick.
      if (screen === 'unknown' && !isUnknownCaseCreation() && !vm.send.hubAdminChecked) return false;
      return isCaseAnswered();
    }

    // Criterion 16 offers Try again after a refusal; criterion 14 makes the confirmation repeat
    // that verb.
    function sendButtonLabel() {
      return sendScreen() === 'notSent' ? 'HubSyncRepairTryAgain' : 'HubSyncRepairSendToHub';
    }

    // Criteria 13 and 14: names the letter, the patient and the hospital, says it cannot be undone,
    // and its button repeats the verb of the button that opened it. It does not list the payload.
    // The final letter also names the bill it asks the Hub for [HM-11242 decision 12]. The
    // cancellation names the Hub case it closes, and why a rejected letter closes as Cancelled
    // [HM-11243 decision 4].
    function confirmSend() {
      if (!canSendLetter()) return;

      let question;
      if (isCancellation()) question = cancellationQuestion();
      else if (isQueryReply()) question = queryReplyQuestion();
      else question = letterQuestion();

      abp.message.confirm(
        question,
        App.localize('HubSyncRepairConfirmTitle'),
        (isConfirmed) => {
          if (!isConfirmed) return;
          // The dialog's callback runs outside the Angular digest.
          $scope.$apply(sendLetter);
        },
        {
          confirmButtonColor: '#1AB394',
          confirmButtonText: App.localize(sendButtonLabel()),
          cancelButtonText: App.localize('Cancel'),
        }
      );
    }

    function cancellationQuestion() {
      const question = App.localize(
        'HubSyncRepairConfirmCancellation',
        vm.lookedUpLetterNumber,
        vm.letter.patientName,
        vm.letter.hospitalName,
        vm.letter.hubCaseId
      );
      return vm.letter.status === enumGuaranteeLetterStatus.Rejected.id
        ? `${question} ${App.localize('HubSyncRepairConfirmCancellationRejected')}`
        : question;
    }

    // [HM-11244 decision 7] The page shows the message itself; the dialog names the query and its thread.
    function queryReplyQuestion() {
      const query = selectedQuery();
      return App.localize(
        'HubSyncRepairConfirmQueryReply',
        query.queryNumber,
        vm.lookedUpLetterNumber,
        query.replyPreview.communicationId
      );
    }

    function letterQuestion() {
      return isFinalLetter()
        ? App.localize(
            'HubSyncRepairConfirmFinal',
            vm.lookedUpLetterNumber,
            vm.letter.patientName,
            vm.letter.hospitalName,
            vm.letter.finalBillAmount == null ? NO_VALUE : $filter('number')(vm.letter.finalBillAmount, 2)
          )
        : App.localize(
            'HubSyncRepairConfirmInitial',
            vm.lookedUpLetterNumber,
            vm.letter.patientName,
            vm.letter.hospitalName
          );
    }

    function sendLetter() {
      const s = vm.send;
      if (sendScreen() === 'unknown' && (s.hubAdminChecked || isUnknownCaseCreation())) {
        s.hubAdminConfirmed = true;
      }
      const input = {
        letterNumber: vm.lookedUpLetterNumber,
        hubAdminChecked: s.hubAdminConfirmed,
      };
      if (s.needsCaseAnswer) {
        input.caseAnswer = s.caseAnswer;
        if (s.caseAnswer === CASE_ANSWER.UseExisting) input.hubCaseId = s.hubCaseId.trim();
      }

      s.sending = true;
      let request;
      if (isQueryReply()) {
        input.serviceRequestQueryId = vm.queryId;
        request = hubSyncRepairSvc.sendQueryReplyAsync(input);
      } else if (isCancellation()) request = hubSyncRepairSvc.sendCancellationAsync(input);
      else if (isFinalLetter()) request = hubSyncRepairSvc.sendFinalLetterAsync(input);
      else request = hubSyncRepairSvc.sendInitialLetterAsync(input);
      request
        .success(showResult)
        .finally(() => {
          s.sending = false;
        });
    }

    function showResult(result) {
      const s = vm.send;
      s.result = result;
      s.hubAdminChecked = false;
      s.recorded = false;

      // Ask again: the server needs the answer, or the Hub refused the operator's case id
      // (criteria 5, 6). The typed id stays, so the operator can correct it rather than retype it.
      const refusedOperatorCase =
        result.status === SEND_STATUS.Failed &&
        result.outcome === OUTCOME.Rejected &&
        result.caseIdFromOperator;
      if (result.status === SEND_STATUS.NeedsCaseAnswer || refusedOperatorCase) {
        s.needsCaseAnswer = true;
      }
      if (sendScreen() === 'unknown') {
        // Unanswered case creation: whether the case is there is exactly what the operator checks,
        // so ask alongside the tick. An unanswered letter to the operator's case goes back to that
        // case, so the question would mislead.
        s.needsCaseAnswer = result.failedStep === STEP.CreateCase;
        s.hubAdminConfirmed = false;
      }
    }

    // Criterion 15: what was sent, and nothing else.
    function sentMessage() {
      if (isQueryReply()) return App.localize('HubSyncRepairSentQueryReply', selectedQuery().queryNumber);
      let key = 'HubSyncRepairSentInitial';
      if (isCancellation()) key = 'HubSyncRepairSentCancellation';
      else if (isFinalLetter()) key = 'HubSyncRepairSentFinal';
      return App.localize(key, vm.lookedUpLetterNumber);
    }

    function unknownCheckMessage() {
      return App.localize('HubSyncRepairUnknownCheck', vm.lookedUpLetterNumber);
    }

    // HM-11240 criterion 9: our record first.
    function alreadySentMessage() {
      const r = vm.send.result;
      const when = $filter('momentFormat')(r.lockedAt, 'LL');
      return r.lockedBy === OUTCOME.ConfirmedByOperator
        ? App.localize('HubSyncRepairAlreadySentConfirmed', when)
        : App.localize('HubSyncRepairAlreadySentAccepted', when);
    }

    // The unanswered step was creating the case. Whether the case is there is what the operator
    // checks in Hub admin, so the case question is the check: no tick, no "record as received".
    function isUnknownCaseCreation() {
      const r = vm.send.result;
      return sendScreen() === 'unknown' && !!r && r.failedStep === STEP.CreateCase;
    }

    function canRecordAsReceived() {
      return !vm.send.sending && !vm.send.recording;
    }

    // HM-11240 decision 2: nothing is sent; the step locks like Accepted. Only offered for an
    // unanswered letter submission: an unanswered case creation is answered by the case question.
    function recordAsReceived() {
      if (!canRecordAsReceived()) return;

      const s = vm.send;
      s.recording = true;
      hubSyncRepairSvc
        .recordAsReceivedAsync({
          letterNumber: vm.lookedUpLetterNumber,
          // The picked action: initial or final letter, or the cancellation [HM-11242, HM-11243].
          action: _.find(ACTIONS, (a) => a.key === vm.action).id,
          // Only that query locks [HM-11244].
          serviceRequestQueryId: isQueryReply() ? vm.queryId : null,
        })
        .success(() => {
          s.result = null;
          s.recorded = true;
        })
        .finally(() => {
          s.recording = false;
        });
    }

    // Back to an empty look-up.
    function checkAnotherLetter() {
      vm.letterNumber = null;
      setLetter(null, null);
    }

    function copyHubCaseId() {
      if (!vm.letter || !vm.letter.hubCaseId || !navigator.clipboard) return;

      navigator.clipboard.writeText(vm.letter.hubCaseId).then(() => {
        abp.notify.info(App.localize('HubSyncRepairCopied'));
      });
    }
  }
})();
